import { useQuery } from '@apollo/client'
import { useState } from 'react'
import { Autocomplete, ListBoxItem, useFilter } from 'react-aria-components'
import { useParams } from 'react-router-dom'
import { GET_FACILITY_PRIMARY_USE_QUERY } from '~/client/queries'
import Panel from '~/components/UIKit/Panel'
import detailedObstructions from '~/config/detailedObstructions'
import { PrimaryUses, primaryUses } from '~/config/facility'
import LayerKeys from '~/config/layerKeys'
import { useAppDispatch, useAppSelector } from '~/store/hooks'
import { setCurrentLayer } from '~/store/layers'
import { DETAILED_OBSTRUCTION_PANEL } from '~/store/panel/types'
import { setActiveTool } from '~/store/tools'
import { GridBox } from '~/ui/ListBox'
import { SearchField } from '~/ui/SearchField'
import { Select, SelectItem } from '~/ui/Select'
import { camelToTitleCase } from '~/utils/string'

const allUses = [{ label: 'All Categories', value: 'ALL_CATEGORIES' }].concat(primaryUses)

export default function DetailedObstructionPanel() {
  const isTouchUI = useAppSelector(state => state.userInterface.isTouchUI)
  const { contains } = useFilter({ sensitivity: 'base' })
  const [searchText, setSearchText] = useState('')
  const params = useParams<{ id: string }>()
  const { data } = useQuery(GET_FACILITY_PRIMARY_USE_QUERY, {
    variables: {
      id: params.id ?? '',
    },
  })
  const [primaryUse, setPrimaryUse] = useState<PrimaryUses | 'ALL_CATEGORIES' | null>(
    data?.facility?.primaryUse ?? null
  )
  const dispatch = useAppDispatch()

  const filteredObstructions = detailedObstructions
    .filter(
      obstruction =>
        contains(obstruction.obstructionType, searchText) ||
        contains(camelToTitleCase(obstruction.obstructionType), searchText) ||
        obstruction.tags.some(tag => contains(tag, searchText))
    )
    .filter(
      obstruction =>
        primaryUse === 'ALL_CATEGORIES' || (primaryUse && obstruction.primaryUses.includes(primaryUse)) || !primaryUse
    )

  return (
    <Panel
      title="Detailed Obstructions"
      alignment="right"
      docked
      scrollable
      panelKey={DETAILED_OBSTRUCTION_PANEL}
      hasToolbar={isTouchUI}
    >
      <div className="p-3 flex flex-col gap-3">
        <Select
          name="primaryUse"
          label="Primary Use"
          selectedKey={primaryUse}
          onSelectionChange={value => setPrimaryUse(value as PrimaryUses)}
        >
          {allUses.map(({ value, label }) => (
            <SelectItem key={label} id={value}>
              {label}
            </SelectItem>
          ))}
        </Select>
        <Autocomplete>
          <SearchField label="Search Obstructions" value={searchText} onChange={setSearchText} />
          <GridBox
            items={filteredObstructions}
            aria-label="Detailed Obstructions"
            className="pt-2"
            renderEmptyState={() => <div>No results found.</div>}
          >
            {item => (
              <ListBoxItem
                id={item.obstructionType}
                textValue={item.obstructionType}
                onAction={() => {
                  dispatch(setCurrentLayer({ layerKey: LayerKeys.OBSTRUCTIONS }))
                  dispatch(
                    setActiveTool({
                      tool: 'OBSTRUCTION_TOOL',
                      props: {
                        ...item,
                        rotation: {
                          x: 0,
                          y: 0,
                          z: 0,
                        },
                      },
                    })
                  )
                }}
                className="-outline-offset-2 cursor-pointer"
              >
                <img
                  src={`/obstructions/${item.obstructionType}.png`}
                  alt={item.obstructionType}
                  className="aspect-square"
                />
                <p className="text-center">{camelToTitleCase(item.obstructionType)}</p>
              </ListBoxItem>
            )}
          </GridBox>
        </Autocomplete>
      </div>
    </Panel>
  )
}
